/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {SelectionModel} from '@angular/cdk/collections';
import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../../common/constants';
import {ColumnHeader} from '../../../../core/models/column-header.model';
import {Arch} from '../../../../models/arch.model';
import {ProgramCodeDTO} from '../../../../models/program-code-dto.model';
import {ArchDevianceService} from '../../../../services/arch-deviance.service';

@Component({
    selector: 'app-edit-arch-deviance-pop-up',
    templateUrl: './edit-arch-deviance-pop-up.component.html',
    styleUrls: ['./edit-arch-deviance-pop-up.component.scss'],
    standalone: false
})
export class EditArchDeviancePopUpComponent implements OnInit {

  showSpinner: boolean;
  noData: boolean;
  updatePrgmCodeList: boolean;
  pageIndex: number;
  pageSize: number;
  tableLength: number;
  dataSource: MatTableDataSource<Arch> = new MatTableDataSource(null);
  selection = new SelectionModel<Arch>(false, []);
  displayedColumns: string[];
  archList: any;
  archHeaders: ColumnHeader[] = [
    {name: 'arch', label: 'Architecture'},
    {name: 'TABLE_SOURCE', label: 'Table Source'}
  ];

  constructor(@Inject(MAT_DIALOG_DATA) public data: ProgramCodeDTO,
              public snackBar: FabMessageService,
              public dialogRef: MatDialogRef<EditArchDeviancePopUpComponent>,
              public archDevianceService: ArchDevianceService) {
  }

  ngOnInit(): void {
    this.displayedColumns = this.archHeaders.map(header => header.name);
    this.noData = true;
    this.showSpinner = true;
    this.updatePrgmCodeList = false;
    this.pageIndex = 0;
    this.pageSize = 10;
    this.getArchByProgramCode();
  }

  /**
   * Calls service to retrieve architecture values by program code
   */
  getArchByProgramCode(): void {
    this.archDevianceService.getArchByProgramCode(this.data.prgmCode).subscribe({
      next: (values) => {
        this.archList = values;
      },
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.setPageData();
      }
    });

  }

  /**
   * calls service to update the program code table arch value
   * @param prgmCode program code
   * @param arch architecture
   */
  setSelectedArch(prgmCode: string, arch: string): void {
    this.showSpinner = true;
    this.archDevianceService.updateArchDeviances(prgmCode, arch).subscribe({
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.snackBar.displaySuccess(`${prgmCode}'s architecture updated to ${arch}.`, DISMISS_MSG);
        this.showSpinner = false;
        this.updatePrgmCodeList = true;
        this.onCancel();
      }
    });
  }

  /**
   * Close popup window.
   */
  onCancel(): void {
    this.dialogRef.close({
      updatePrgmCodeList: this.updatePrgmCodeList
    });
  }

  /**
   * Event handler for page changing
   */
  handlePage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.setPageData();
  }

  /**
   * Set the paginated data for the table depending on what page is selected
   */
  setPageData(): void {
    const end = (this.pageIndex + 1) * this.pageSize;
    const start = this.pageIndex * this.pageSize;
    const paginatedData = this.archList !== undefined ? this.archList.slice(start, end) : [];
    this.tableLength = this.archList !== undefined ? this.archList.length : 0;
    this.dataSource = new MatTableDataSource(paginatedData);
    this.noData = false;
    this.showSpinner = false;
  }
}
